import { apiFetch, readJson } from './client';
import { mapProfileActivity, type UserProfile, type UserProfileActivity } from '@jarvis/platform-ui';

export type { UserProfile, UserProfileActivity };

export interface MeResponse {
  authenticated: boolean;
  username: string | null;
  displayName: string | null;
  roles: string[];
  highestOpsRole: string | null;
}

/** Bootstrap current Entra-backed BFF session (WebOpsApi cookie). */
export async function fetchMe(): Promise<MeResponse> {
  const res = await apiFetch('/api/auth/me');
  if (!res.ok) {
    return {
      authenticated: false,
      username: null,
      displayName: null,
      roles: [],
      highestOpsRole: null
    };
  }
  return readJson<MeResponse>(res);
}

/** Full-page navigation — OIDC handled by WebOpsApi. */
export function signinUrl(returnPath = '/'): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const absoluteReturn = returnPath.startsWith('http')
    ? returnPath
    : `${origin}${returnPath.startsWith('/') ? returnPath : `/${returnPath}`}`;
  return `/signin?returnUrl=${encodeURIComponent(absoluteReturn)}`;
}

export function signoutUrl(): string {
  return '/signout';
}

export async function fetchProfile(): Promise<UserProfile> {
  const res = await apiFetch('/api/auth/profile');
  if (!res.ok) throw new Error(`Failed to load profile: ${res.status}`);
  const raw = await readJson<{
    objectId?: string | null;
    userPrincipalName?: string | null;
    displayName?: string | null;
    mail?: string | null;
    tenantId?: string | null;
    tenantDomain?: string | null;
    roles?: string[];
    highestRole?: string | null;
    highestOpsRole?: string | null;
    accountEnabled?: boolean | null;
    department?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    officeLocation?: string | null;
    employeeId?: string | null;
    mobilePhone?: string | null;
    hasPhoto?: boolean | null;
    sources?: string[];
  }>(res);
  return {
    objectId: raw.objectId ?? null,
    userPrincipalName: raw.userPrincipalName ?? null,
    displayName: raw.displayName ?? null,
    mail: raw.mail ?? null,
    tenantId: raw.tenantId ?? null,
    tenantDomain: raw.tenantDomain ?? null,
    roles: raw.roles ?? [],
    highestRole: raw.highestRole ?? raw.highestOpsRole ?? null,
    accountEnabled: raw.accountEnabled ?? null,
    department: raw.department ?? null,
    jobTitle: raw.jobTitle ?? null,
    company: raw.company ?? null,
    officeLocation: raw.officeLocation ?? null,
    employeeId: raw.employeeId ?? null,
    mobilePhone: raw.mobilePhone ?? null,
    hasPhoto: raw.hasPhoto ?? null,
    sources: raw.sources ?? ['claims']
  };
}

export async function fetchProfileActivity(): Promise<UserProfileActivity> {
  const res = await apiFetch('/api/auth/profile/activity');
  if (!res.ok) throw new Error(`Failed to load profile activity: ${res.status}`);
  return mapProfileActivity((await readJson(res)) as Record<string, unknown>);
}
